import React, { createContext, useContext, useState, ReactNode } from 'react';

export type DataMode = 'accumulated' | '5min';

interface DataModeContextType {
  dataMode: DataMode;
  setDataMode: (mode: DataMode) => void;
  toggleDataMode: () => void;
  effectiveDataMode: DataMode;
  disableGlobalMode: () => void;
  enableGlobalMode: () => void;
}

const DataModeContext = createContext<DataModeContextType | undefined>(undefined);

export { DataModeContext };

export const useDataMode = () => {
  const context = useContext(DataModeContext);
  if (context === undefined) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
};

interface DataModeProviderProps {
  children: ReactNode;
}

export const DataModeProvider: React.FC<DataModeProviderProps> = ({ children }) => {
  const [dataMode, setDataMode] = useState<DataMode>(() => {
    if (typeof localStorage === 'undefined') return 'accumulated';
    const stored = localStorage.getItem('mmec_rtl_data_mode');
    return stored === '5min' || stored === 'accumulated' ? stored : 'accumulated';
  });
  
  // Use the selected data mode (both 5min and accumulated are now valid)
  const effectiveDataMode: DataMode = dataMode;
  
  const toggleDataMode = () => {
    setDataMode(prevMode => prevMode === 'accumulated' ? '5min' : 'accumulated');
  };
  
  const disableGlobalMode = () => {
    // Implementation for disabling global mode
    console.log('Global mode disabled');
  };
  
  const enableGlobalMode = () => {
    // Implementation for enabling global mode
    console.log('Global mode enabled');
  };
  
  const value: DataModeContextType = {
    dataMode,
    setDataMode,
    toggleDataMode,
    effectiveDataMode,
    disableGlobalMode,
    enableGlobalMode
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
}; 